import { isArray } from '../shortcut'

import type {
  MixtureMemberDecorator,
  TakeArgs,
  TFunction,
  TNil,
  TRecord
} from '../types'

interface IMemberDecoratorContext<
  T extends any[] = any[],
  U extends any[] = any[]
> {
  argsProcess?(...args: T): TNil | U
  esAccessorDecorator?(
    arg1: any,
    context: ClassAccessorDecoratorContext,
    ...args: U
  ): ClassAccessorDecoratorResult<any, any>
  esFieldDecorator?(
    arg1: any,
    context: ClassFieldDecoratorContext,
    ...args: U
  ): any
  esGetterDecorator?(
    arg1: any,
    context: ClassGetterDecoratorContext,
    ...args: U
  ): any
  esMethodDecorator?(
    arg1: any,
    context: ClassMethodDecoratorContext,
    ...args: U
  ): any
  esSetterDecorator?(
    arg1: any,
    context: ClassSetterDecoratorContext,
    ...args: U
  ): any
  tsDecorator?(
    prototype: any,
    key: string,
    descriptor?: PropertyDescriptor & TRecord,
    ...args: U
  ): (PropertyDescriptor & TRecord) | TNil
}

export function createMemberDecorator<T = [], U = T>(
  context: IMemberDecoratorContext<TakeArgs<T>, TakeArgs<U>>
): T extends TFunction ? T : (...args: TakeArgs<T>) => MixtureMemberDecorator
export function createMemberDecorator(context: IMemberDecoratorContext): any {
  return (...args: any[]) => {
    if (context.argsProcess !== undefined) {
      const processed = context.argsProcess(...args)
      if (isArray(processed)) args = processed
      else return
    }
    return (arg1: any, arg2: any, arg3?: any) => {
      if (typeof arg2 === 'string' || typeof arg2 === 'symbol') {
        if (context.tsDecorator === undefined)
          throw new Error('The decorator is not supported here.')
        if (typeof arg2 !== 'string')
          throw new Error('The decorator is only supported on string field.')
        return context.tsDecorator(arg1?.prototype ?? arg1, arg2, arg3, ...args)
      } else {
        if (typeof arg2?.name !== 'string')
          throw new Error('The decorator is only supported on string field.')
        switch (arg2?.kind as ClassMemberDecoratorContext['kind'] | undefined) {
          case 'accessor': {
            if (context.esAccessorDecorator === undefined)
              throw new Error('The decorator is not supported here.')
            return context.esAccessorDecorator(arg1, arg2, ...args)
          }
          case 'field': {
            if (context.esFieldDecorator === undefined)
              throw new Error('The decorator is not supported here.')
            return context.esFieldDecorator(arg1, arg2, ...args)
          }
          case 'getter': {
            if (context.esGetterDecorator === undefined)
              throw new Error('The decorator is not supported here.')
            return context.esGetterDecorator(arg1, arg2, ...args)
          }
          case 'method': {
            if (context.esMethodDecorator === undefined)
              throw new Error('The decorator is not supported here.')
            return context.esMethodDecorator(arg1, arg2, ...args)
          }
          case 'setter': {
            if (context.esSetterDecorator === undefined)
              throw new Error('The decorator is not supported here.')
            return context.esSetterDecorator(arg1, arg2, ...args)
          }
          default: {
            throw new Error('The decorator is not supported here.')
          }
        }
      }
    }
  }
}
